import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, ChevronLeft, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { displayTime, fmtDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Availability() {
  const nav = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState(null);   // selected
  const [weekly, setWeekly] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openOverride, setOpenOverride] = useState(false);
  const [orDate, setOrDate] = useState(new Date());
  const [orUnavail, setOrUnavail] = useState(false);
  const [orFrom, setOrFrom] = useState("07:00");
  const [orTo, setOrTo] = useState("10:00");

  // Load facilities once, pick primary as default.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/facilities");
        const items = data.items || [];
        setFacilities(items);
        const primary = items.find((f) => f.is_primary) || items[0];
        if (primary) setFacilityId(primary.id);
      } catch { toast.error("Failed to load facilities"); }
    })();
  }, []);

  // Load availability whenever the selected facility changes.
  useEffect(() => {
    if (!facilityId) return;
    (async () => {
      try {
        const { data } = await api.get(`/availability?facility_id=${facilityId}`);
        setWeekly(data.weekly);
        setOverrides(data.overrides);
      } catch { toast.error("Failed to load availability"); }
    })();
  }, [facilityId]);

  const toggleDay = (idx) => setWeekly(weekly.map((d, i) => i === idx ? { ...d, enabled: !d.enabled } : d));
  const updateSlot = (di, si, k, v) => setWeekly(weekly.map((d, i) => i === di ? { ...d, slots: d.slots.map((s, j) => j === si ? { ...s, [k]: v } : s) } : d));
  const addSlot = (di) => setWeekly(weekly.map((d, i) => i === di ? { ...d, slots: [...d.slots, { from: "14:00", to: "18:00" }] } : d));
  const removeSlot = (di, si) => setWeekly(weekly.map((d, i) => i === di ? { ...d, slots: d.slots.filter((_, j) => j !== si) } : d));

  const save = async () => {
    if (!facilityId) { toast.error("Add a facility first from Profile → Facilities"); return; }
    setBusy(true);
    try { await api.put("/availability", { weekly, overrides, facility_id: facilityId }); toast.success("Availability saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const applyOverride = () => {
    const dateStr = orDate.toISOString().slice(0, 10);
    const existing = overrides.filter((o) => o.date !== dateStr);
    existing.push({ date: dateStr, unavailable: orUnavail, slots: orUnavail ? [] : [{ from: orFrom, to: orTo }] });
    setOverrides(existing);
    setOpenOverride(false);
    toast.success("Override added — remember to update.");
  };
  const removeOverride = (date) => setOverrides(overrides.filter((o) => o.date !== date));

  return (
    <Layout>
      <div className="card-soft overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-[#EFECE5]">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-[#47554D] hover:text-[#0A4C36]"><ChevronLeft size={16} /> Back</button>
          <h1 className="text-xl font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Availability</h1>
          <button onClick={save} disabled={busy} className="btn-emerald" data-testid="avail-update-btn">{busy ? "Saving…" : "Update"}</button>
        </div>

        {/* Facility switcher — pill tabs across all active facilities */}
        <div className="px-6 py-4 border-b border-[#EFECE5] bg-[#F7F4EC]/50" data-testid="avail-facility-switcher">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-[#5C7568] mr-1"><Building2 size={12} className="inline mr-1" /> Facility</span>
            {facilities.filter((f) => f.active).map((f) => (
              <button
                key={f.id}
                onClick={() => setFacilityId(f.id)}
                data-testid={`facility-pill-${f.id}`}
                className={`pill-tab ${facilityId === f.id ? "pill-tab-active" : "pill-tab-inactive"}`}
              >
                {f.name}
                {f.is_primary && <span className="ml-1 text-[9px] font-bold uppercase opacity-70">Primary</span>}
              </button>
            ))}
            <button onClick={() => nav("/facilities")} className="text-xs text-[#0A4C36] hover:underline ml-1" data-testid="manage-facilities-link">
              Manage facilities →
            </button>
          </div>
          {facilities.length === 0 && (
            <div className="text-sm text-[#8A9992] mt-2">No facilities yet — <button onClick={() => nav("/facilities")} className="text-[#0A4C36] underline">add your first facility</button>.</div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-8 p-8">
          <div className="md:col-span-2">
            <div className="field-label mb-4">Working hours</div>
            <div className="space-y-4">
              {weekly.map((d, i) => (
                <div key={d.day} className="flex flex-col md:flex-row md:items-center gap-3 py-2 border-b border-dashed border-[#EFECE5]">
                  <label className="w-24 flex items-center gap-2 text-sm text-[#1F2A24]">
                    <input type="checkbox" checked={d.enabled} onChange={() => toggleDay(i)} data-testid={`day-toggle-${d.day}`} className="w-4 h-4 accent-[#0A4C36]" />
                    <span className="font-medium">{d.day}</span>
                  </label>
                  {d.enabled ? (
                    <div className="flex-1 space-y-2">
                      {d.slots.map((s, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <input type="time" value={s.from} onChange={(e) => updateSlot(i, j, "from", e.target.value)} className="input-clean w-32" data-testid={`slot-${d.day}-${j}-from`} />
                          <span className="text-[#8A9992]">–</span>
                          <input type="time" value={s.to} onChange={(e) => updateSlot(i, j, "to", e.target.value)} className="input-clean w-32" data-testid={`slot-${d.day}-${j}-to`} />
                          {d.slots.length > 1 && <button onClick={() => removeSlot(i, j)} className="text-red-500"><X size={14} /></button>}
                          {j === d.slots.length - 1 && <button onClick={() => addSlot(i)} className="w-9 h-9 rounded-full bg-[#0A4C36] text-white flex items-center justify-center"><Plus size={14} /></button>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex-1 text-sm text-[#8A9992]">Unavailable</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="field-label mb-3">Add overrides</div>
            <p className="text-sm text-[#6C7B72] mb-4">Add dates when your availability changes from your weekly hours.</p>
            <button className="btn-outline-emerald w-full" onClick={() => setOpenOverride(true)} data-testid="add-override-btn">Add a date override</button>
            <div className="mt-4 space-y-2">
              {overrides.map((o) => (
                <div key={o.date} className="flex items-center justify-between bg-white border border-[#EFECE5] rounded-2xl px-3 py-2">
                  <div>
                    <div className="text-sm font-medium">{fmtDate(o.date)}</div>
                    <div className="text-xs text-[#8A9992]">{o.unavailable ? "Unavailable" : (o.slots?.[0] ? `${displayTime(o.slots[0].from)} – ${displayTime(o.slots[0].to)}` : "—")}</div>
                  </div>
                  <button onClick={() => removeOverride(o.date)} className="text-red-500"><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={openOverride} onOpenChange={setOpenOverride}>
        <DialogContent className="rounded-[24px] max-w-md" data-testid="override-dialog">
          <DialogHeader><DialogTitle className="text-lg text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Select the date(s) and assign your preferred hours</DialogTitle></DialogHeader>
          <Calendar mode="single" selected={orDate} onSelect={(d) => d && setOrDate(d)} className="rounded-xl border-[#EFECE5]" />
          <div>
            <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" className="accent-[#0A4C36]" checked={orUnavail} onChange={(e) => setOrUnavail(e.target.checked)} /> Unavailable</label>
          </div>
          {!orUnavail && (
            <div className="flex items-center gap-2 mt-2">
              <input type="time" value={orFrom} onChange={(e) => setOrFrom(e.target.value)} className="input-clean w-40" />
              <span className="text-[#8A9992]">–</span>
              <input type="time" value={orTo} onChange={(e) => setOrTo(e.target.value)} className="input-clean w-40" />
            </div>
          )}
          <div className="flex justify-end mt-4"><button onClick={applyOverride} className="btn-emerald">Apply</button></div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
